import { useMemo, useCallback, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import CustomTable from "../../../components/CustomTable/CustomTable";
import CustomModal from "../../../components/CustomModal/CustomModal";
import ActionReasonModal from "../../../components/ActionReasonModal/ActionReasonModal";
import {
  ROLE_MODAL_WIDTHS, BTN_TXT, INVESTOR_SUCCESS_MESSAGES,
} from "../../../constants/constants";
import {
  deactivateOrg,
  reactivateOrg,
  sendKycRemainder,
  rejectDoc
} from "../../../redux/slices/EnqMngtSlice";
import { getInvestorView, getInvDeactivateStatus, approveBoardResolution } from "../../../redux/slices/investorSlice";
import { message, Spin, Flex } from "antd";
import { getInvestorColumns } from "./getInvestorColumns";
import { useNavigate } from "react-router-dom";
import { count } from "../../../redux/slices/dealsSlice";
import { formatCurrency } from "../../../helpers/helpers";
import ErrorAlert from "../../../components/ErrorAlert/ErrorAlert";
import DangerButton from "../../../components/CustomButton/DangerButton";
import PrimaryButton from "../../../components/CustomButton/PrimaryButton";
import {
  TABLE_COLUMN_DEFAULTS,
  buildTableScreenKey,
} from "../../../constants/tableColumnDefaults";

const InvestorTable = ({ tableData, selectedStatus, onColumnControlChange, pagination }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { commentSubmitError, commentSubmitLoading, rejectDocError } = useSelector(
    (state) => state?.enqMngt
  );
  const { approveBRLoading, approveBRError } = useSelector((state) => state?.investor);
  const userPrivileges = useMemo(
    () => user?.privilege?.map((p) => p.privilegeCode) || [],
    [user]
  );
  const { loading, deactivateStatus, hasInvDetsLoading } = useSelector((state) => state?.investor);
  const [modal, setModal] = useState({ type: null, data: null });
  const [viewBR, setViewBR] = useState({ open: false, data: null });

  const closeModal = useCallback(() => setModal({ type: null, data: null }), []);

  const handleReviewManualKyc = useCallback((record) => {
    const orgId = typeof record === "object" ? record.orgId : record;
    const data = typeof record === "object" ? record : null;
    navigate(`/investor/review-manual-kyc/${orgId}`, {
      state: { investorData: data }
    });
  }, [navigate]);

  const openModal = useCallback((type, investor) => {
    if (type === "deactivate") {
      dispatch(getInvDeactivateStatus(investor.orgId));
    }
    setModal({ type, data: investor });
  }, [dispatch]);

  // 🔹 Handle Deactivate / Reactivate
  const handleReasonSubmit = useCallback(
    async (values) => {
      if (!modal.type || !modal.data?.orgId) return;

      if (modal.type === "reject") {
        const response = await dispatch(
          rejectDoc({
            docId: viewBR?.data?.boardResolution?.docId,
            rejectReason: values.reason,
          })
        );

        if (response.payload.statusCode === 201) {
          closeModal();
          setViewBR({ open: false, data: null });
          dispatch(getInvestorView(selectedStatus));
        }

        return;
      }

      let action;
      if (modal.type === "deactivate") action = deactivateOrg;
      else if (modal.type === "activate") action = reactivateOrg;

      if (action) {
        const res = await dispatch(
          action({
            orgId: modal.data.orgId,
            comments: values.reason,
            modifiedAt: modal.data.modifiedAt,
          })
        );

        if (res.payload?.statusCode === 201) {
          message.success(
            INVESTOR_SUCCESS_MESSAGES[modal.type](
              modal.data?.investorName
            )
          );
          dispatch(getInvestorView(selectedStatus));
          dispatch(count("INVESTOR"));
          closeModal();
        } else {
          message.error("Action failed, please try again.");
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch, modal, closeModal, selectedStatus]
  );

  // 🔹 Direct API call for Send KYC Reminder
  const handleSendKycReminder = useCallback(
    async (investor) => {
      const response = await dispatch(sendKycRemainder(investor.orgId));
      if (response?.payload?.status === "success") {
        message.success("KYC Reminder sent successfully!");
      } else {
        message.error("Failed to send KYC Reminder.");
      }
    },
    [dispatch]
  );

  const columns = useMemo(
    () =>
      getInvestorColumns({
        onView: (id) => navigate(`/investor/inv-details/${id}/${selectedStatus}`),
        onDeactivate: (investor) => openModal("deactivate", investor),
        onActivate: (investor) => openModal("activate", investor),
        onSendKycReminder: handleSendKycReminder,
        onBRView: (investor) => setViewBR({ open: true, data: investor }),
        onReviewManualKyc: handleReviewManualKyc,
        status: selectedStatus,
        privileges: userPrivileges
      }),
    [openModal, selectedStatus, handleSendKycReminder, navigate, userPrivileges, handleReviewManualKyc]
  );

  const handleApproveDoc = useCallback(async () => {

    const payload = {
      orgId: viewBR?.data?.orgId,
      docId: viewBR?.data?.boardResolution?.docId
    }

    const res = await dispatch(approveBoardResolution(payload));
    if (res.payload?.statusCode === 201) {
      message.success("Board Resolution approved successfully!");
      dispatch(getInvestorView(selectedStatus));
      setViewBR({ open: false, data: null });
      dispatch(count("INVESTOR"));
    } else {
      message.error("Failed to approve Board Resolution.");
    }
  }, [dispatch, viewBR, selectedStatus]);

  return (
    <>
      <CustomTable
        rowKey="orgId"
        columns={columns}
        dataSource={tableData}
        loading={loading}
        screenKey={buildTableScreenKey("investor")}
        defaultColumnKeys={TABLE_COLUMN_DEFAULTS.investor}
        onColumnControlChange={onColumnControlChange}
        pagination={pagination}
      />

      <ActionReasonModal
        modalType={modal.type}
        onClose={closeModal}
        onSubmit={handleReasonSubmit}
        submitLoading={commentSubmitLoading}
        submitError={commentSubmitError}
      >
        <Spin spinning={hasInvDetsLoading}>
          {modal.type === "deactivate" &&
            deactivateStatus &&
            (
              deactivateStatus?.dealsInvested?.length > 0 ||
              Number(deactivateStatus?.investorBank?.amount) > 0
            ) && (
              <div
                style={{
                  background: "#FFF7E6",
                  border: "1px solid #FFD591",
                  padding: 12,
                  borderRadius: 6,
                  marginBottom: 15,
                  fontSize: 13,
                  color: "#8C5A00",
                }}
              >
                <strong>Note:</strong> This investor has financial dependencies.
                <div
                  style={{
                    marginTop: 8,
                    fontSize: 13,
                    color: "#8C5A00",
                  }}
                >
                  {deactivateStatus?.dealsInvested?.length > 0 && (
                    <span>
                      <strong>Ongoing Deals:</strong>{" "}
                      {deactivateStatus.dealsInvested.length}
                    </span>
                  )}
                  <br />
                  {Number(deactivateStatus?.investorBank?.amount) > 0 && (
                    <span>
                      <strong>Account Balance:</strong>{" "}
                      {formatCurrency(deactivateStatus.investorBank.amount)}
                    </span>
                  )}
                </div>
              </div>
            )}
        </Spin>
      </ActionReasonModal>

      {viewBR.open && (
        <CustomModal
          title="Board Resolution Document"
          width={ROLE_MODAL_WIDTHS}
          open
          centered
          closeIcon
          onCancel={() => setViewBR({ open: false, data: null })}
          content={
            <>
              <iframe
                src={viewBR.data?.boardResolution?.docUrl}
                title="PDF Preview"
                style={{ width: "100%", height: "60vh", border: "none" }}
              />

              <Flex
                gap={10}
                justify="space-between"
                style={{
                  marginTop: 20,
                  position: "sticky",
                  padding: 10,
                }}
              >
                <div>
                  {(approveBRError || rejectDocError) && <ErrorAlert error={approveBRError || rejectDocError} />}
                </div>
                <Flex gap={10}>
                  <DangerButton
                    isSolid={true}
                    disabled={viewBR.data?.boardResolution?.rejected}
                    onClick={() => openModal("reject", viewBR.data)}
                  >
                    {BTN_TXT.REJECT}
                  </DangerButton>
                  <PrimaryButton
                    loading={approveBRLoading}
                    onClick={handleApproveDoc}
                  >
                    {BTN_TXT.APPROVE}
                  </PrimaryButton>
                </Flex>
              </Flex>
            </>
          }
        />
      )}
    </>
  );
};

export default InvestorTable;
